import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clubApi, fmtEur } from "../../lib/clubApi";
import { useClubStats } from "./ClubLayout";
import { Loader2, Mic } from "lucide-react";
import VoiceMemberModal from "../../components/VoiceMemberModal";

// Compact "Nuovo socio" form matching user's mockup:
// Nome + Telefono + Indirizzo + Zona (Pegli/Pra/Sestri/Voltri) + Inizio + Scadenza (mesi) + big black save button
export default function ClubNewMember() {
    const nav = useNavigate();
    const { refresh } = useClubStats();
    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const [form, setForm] = useState({
        name: "",
        phone: "",
        address: "",
        zone: "",
        start: today,
        months: 1,
    });
    const [busy, setBusy] = useState(false);
    const [voiceOpen, setVoiceOpen] = useState(false);

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const applyVoice = (fields) => {
        setForm((f) => ({
            ...f,
            name: fields.name || f.name,
            phone: fields.phone || f.phone,
            address: fields.address || f.address,
            zone: fields.zone || f.zone,
        }));
    };

    const save = async () => {
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Nome e telefono sono obbligatori");
            return;
        }
        setBusy(true);
        try {
            const m = await clubApi.createMember({
                name: form.name.trim(),
                phone: form.phone.trim(),
                address: form.address.trim(),
                zone: form.zone.trim(),
                membership_start: form.start,
                months_paid: Number(form.months) || 1,
            });
            toast.success(`Socio registrato · ${fmtEur(4.9 * (Number(form.months) || 1))} incassati`);
            refresh();
            nav(`/club/clienti/${m.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="club-new-member-page">
            <div className="flex items-center justify-between gap-3">
                <h2 className="font-display font-bold text-2xl text-stone-900">Nuovo socio</h2>
                <button
                    type="button"
                    onClick={() => setVoiceOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-stone-900 text-white text-xs font-bold shadow-sm active:scale-[0.98]"
                    data-testid="new-member-voice-btn"
                >
                    <Mic size={14} /> Voce
                </button>
            </div>

            <p className="text-xs text-stone-500 -mt-1">
                Puoi passare il telefono al cliente e usare la <b>voce</b> per compilare i campi.
            </p>

            <VoiceMemberModal
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                onCaptured={applyVoice}
            />

            <Field label="Nome e cognome">
                <input
                    type="text"
                    value={form.name}
                    onChange={setField("name")}
                    className="input-club-new"
                    data-testid="new-member-name"
                    autoComplete="off"
                />
            </Field>

            <Field label="Telefono">
                <input
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    className="input-club-new"
                    data-testid="new-member-phone"
                    autoComplete="off"
                />
            </Field>

            <Field label="Indirizzo">
                <input
                    type="text"
                    value={form.address}
                    onChange={setField("address")}
                    className="input-club-new"
                    data-testid="new-member-address"
                    autoComplete="off"
                />
            </Field>

            <Field label="Zona">
                <input
                    type="text"
                    value={form.zone}
                    onChange={setField("zone")}
                    placeholder="Pegli / Pra / Sestri / Voltri"
                    className="input-club-new placeholder:text-stone-400"
                    data-testid="new-member-zone"
                    list="club-zone-options"
                    autoComplete="off"
                />
                <datalist id="club-zone-options">
                    <option value="Pegli" />
                    <option value="Pra" />
                    <option value="Sestri" />
                    <option value="Voltri" />
                    <option value="Multedo" />
                    <option value="Cornigliano" />
                    <option value="Sampierdarena" />
                    <option value="Altro" />
                </datalist>
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Inizio">
                    <input
                        type="date"
                        value={form.start}
                        onChange={setField("start")}
                        className="input-club-new"
                        data-testid="new-member-start"
                    />
                </Field>
                <Field label="Scadenza">
                    <select
                        value={form.months}
                        onChange={setField("months")}
                        className="input-club-new bg-white"
                        data-testid="new-member-months"
                    >
                        <option value={1}>1 mese</option>
                        <option value={3}>3 mesi</option>
                        <option value={6}>6 mesi</option>
                        <option value={12}>12 mesi</option>
                    </select>
                </Field>
            </div>

            <p className="text-xs text-stone-500">
                Quota tessera: <b>4,90 €/mese</b> · Totale iniziale <b>{fmtEur(4.9 * (Number(form.months) || 1))}</b>
            </p>

            <button
                onClick={save}
                disabled={busy}
                className="w-full py-4 rounded-2xl bg-stone-900 text-white text-lg font-bold shadow-md active:scale-[0.99] disabled:opacity-50"
                data-testid="new-member-save"
            >
                {busy ? (
                    <span className="inline-flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Salvo…</span>
                ) : "Salva socio"}
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block space-y-1.5">
            <span className="text-base font-bold text-stone-900">{label}</span>
            {children}
        </label>
    );
}
